// Tiny markdown renderer — supports headings, paragraphs, bold, italic,
// inline code, fenced code, lists, links, images, tables and blockquotes.
// Intentionally minimal so we don't pull in a runtime dependency for the
// Help Center.
import React from 'react';
import DOMPurify from 'dompurify';

// SECURITY: every HTML fragment built from user-supplied markdown is
// sanitised before being injected via dangerouslySetInnerHTML. We block
// script-bearing tags/attrs and restrict URL schemes so a help article
// (which is editable by per-tenant admins) can't pivot to stored XSS.
const PURIFY_CONFIG = {
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|data:image\/(?:png|jpeg|gif|webp));|#|\/|\.)/i,
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'style', 'svg', 'math'],
  FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur', 'formaction'],
};
const safe = (html) => DOMPurify.sanitize(html, PURIFY_CONFIG);

const escapeHtml = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const inline = (text) => {
  let t = escapeHtml(text);
  // images ![alt](src)
  t = t.replace(/!\[([^\]]*)\]\(([^)]+)\)/g,
    '<img alt="$1" src="$2" class="my-4 rounded-lg border border-gray-200 shadow-sm max-w-full" />');
  // links [text](href)
  t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noreferrer">$1</a>');
  // bold **x**
  t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // italic *x*
  t = t.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  // inline code `x`
  t = t.replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 rounded bg-gray-100 text-pink-700 text-sm">$1</code>');
  return safe(t);
};

export default function Markdown({ source = '', className = '' }) {
  if (!source) return null;
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // fenced code
    if (/^```/.test(line)) {
      const lang = line.replace(/^```/, '').trim();
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++;
      out.push(
        <pre key={key++} className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm my-4">
          <code>{buf.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // table — header | --- | row
    if (/^\|.+\|/.test(line) && /^\|[\s:|-]+\|/.test(lines[i + 1] || '')) {
      const header = line.split('|').slice(1, -1).map(c => c.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && /^\|.+\|/.test(lines[i])) {
        rows.push(lines[i].split('|').slice(1, -1).map(c => c.trim()));
        i++;
      }
      out.push(
        <div key={key++} className="my-4 overflow-x-auto">
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50 text-gray-700">
              <tr>{header.map((h, j) => (
                <th key={j} className="text-left px-3 py-2 border-b border-gray-200"
                    dangerouslySetInnerHTML={{ __html: inline(h) }} />
              ))}</tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="odd:bg-white even:bg-gray-50">
                  {r.map((c, ci) => (
                    <td key={ci} className="px-3 py-2 border-b border-gray-100 align-top"
                        dangerouslySetInnerHTML={{ __html: inline(c) }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // headings
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const cls = ['text-3xl font-bold mt-6 mb-3 text-gray-900',
                   'text-2xl font-bold mt-6 mb-3 text-gray-900',
                   'text-xl font-semibold mt-5 mb-2 text-gray-900',
                   'text-lg font-semibold mt-4 mb-2 text-gray-900',
                   'text-base font-semibold mt-3 mb-2 text-gray-800',
                   'text-sm font-semibold mt-3 mb-2 text-gray-800'][level - 1];
      const Tag = `h${level}`;
      out.push(<Tag key={key++} className={cls} dangerouslySetInnerHTML={{ __html: inline(h[2]) }} />);
      i++;
      continue;
    }

    // unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      out.push(
        <ul key={key++} className="list-disc pl-6 space-y-1 my-3 text-gray-700">
          {items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}
        </ul>
      );
      continue;
    }

    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      out.push(
        <ol key={key++} className="list-decimal pl-6 space-y-1 my-3 text-gray-700">
          {items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}
        </ol>
      );
      continue;
    }

    // blockquote
    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      out.push(
        <blockquote key={key++} className="border-l-4 border-blue-400 bg-blue-50 px-4 py-2 my-3 text-gray-700"
          dangerouslySetInnerHTML={{ __html: inline(buf.join(' ')) }} />
      );
      continue;
    }

    // hr
    if (/^---+\s*$/.test(line)) {
      out.push(<hr key={key++} className="my-6 border-gray-200" />);
      i++;
      continue;
    }

    // blank line
    if (/^\s*$/.test(line)) { i++; continue; }

    // paragraph (collect until blank/structure)
    const buf = [line];
    i++;
    while (i < lines.length && !/^\s*$/.test(lines[i]) &&
           !/^#{1,6}\s+/.test(lines[i]) && !/^```/.test(lines[i]) &&
           !/^\s*[-*]\s+/.test(lines[i]) && !/^\s*\d+\.\s+/.test(lines[i]) &&
           !/^>\s?/.test(lines[i]) && !/^\|.+\|/.test(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    out.push(
      <p key={key++} className="text-gray-700 leading-relaxed my-3"
         dangerouslySetInnerHTML={{ __html: inline(buf.join(' ')) }} />
    );
  }

  return <div className={className}>{out}</div>;
}
